import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Mail } from "lucide-react";
import SEO from "../components/SEO";

export default function RefundPolicy() {
  return (
    <main
      data-testid="refund-policy-page"
      className="min-h-[calc(100vh-7rem)] bg-cream-200/30 py-14 md:py-20"
    >
      <SEO
        title="Refund Policy"
        description="LuviaRx refund and replacement policy for research-grade compounds. Contact support within 48 hours of delivery for damaged or incorrect items."
        path="/refund-policy"
      />
      <div className="container-x max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-charcoal-400 hover:text-charcoal-600 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
        </Link>

        <p className="overline mb-2 inline-flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-[#8B2252]" strokeWidth={2} />
          Policy
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
          Refund Policy
        </h1>

        <article className="mt-8 space-y-7 text-charcoal-500 leading-relaxed text-[15px]">
          <p>
            At LuviaRx, we stand behind the quality of every compound we carry. Because our
            products are research-grade compounds, all sales are handled with strict guidelines
            to maintain research integrity and product safety.
          </p>

          <Section title="We do not offer refunds for:">
            <ul className="space-y-2 pl-5 list-disc marker:text-[#8B2252]">
              <li>Orders where the customer changed their mind after payment</li>
              <li>Incorrectly placed orders due to customer error</li>
              <li>Opened, reconstituted, or tampered vials</li>
              <li>Products that have been improperly stored after delivery</li>
            </ul>
          </Section>

          <Section title="We do offer refunds or replacements for:">
            <ul className="space-y-2 pl-5 list-disc marker:text-[#8B2252]">
              <li>Products that arrive visibly damaged (photo evidence required within 48 hours of delivery)</li>
              <li>Incorrect items shipped due to our error</li>
              <li>Packages confirmed lost by the carrier via tracking</li>
            </ul>
          </Section>

          <Section title="How to request a refund or replacement:">
            <p>
              Contact our support team at{" "}
              <a
                href="mailto:support@luviarx.com"
                className="text-[#8B2252] font-medium hover:underline"
              >
                support@luviarx.com
              </a>{" "}
              within 48 hours of delivery. Include your order number and clear photo evidence of
              the issue. We will review your request and respond within 1-2 business days.
              Approved refunds will be processed via Zelle back to the original sender. Approved
              replacements will be shipped within 2-3 business days of approval.
            </p>
          </Section>

          <Section title="Important notes:">
            <ul className="space-y-2 pl-5 list-disc marker:text-[#8B2252]">
              <li>All products are sold strictly for research purposes only and are not intended for human consumption</li>
              <li>Refund and replacement requests are reviewed on a case-by-case basis</li>
              <li>LuviaRx reserves the right to decline requests that do not meet the above criteria</li>
              <li>By completing a purchase you agree to these refund terms</li>
            </ul>
          </Section>

          <div className="surface-card p-6 md:p-7 mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="overline mb-1">Need help</p>
              <p className="text-charcoal-600 font-display font-bold text-lg">
                Contact our support team
              </p>
              <p className="text-sm text-charcoal-400">
                We respond within 1-2 business days.
              </p>
            </div>
            <a
              href="mailto:support@luviarx.com"
              data-testid="refund-policy-support-link"
              className="btn-primary self-stretch sm:self-auto"
            >
              <Mail className="w-4 h-4 mr-2" strokeWidth={2} />
              support@luviarx.com
            </a>
          </div>
        </article>
      </div>
    </main>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-display font-bold text-xl text-charcoal-600 mb-3">{title}</h2>
    {children}
  </section>
);
